import React, { useEffect, useCallback } from 'react';
import { type PackageDetails } from '../../services/Clients/S3Client';
import { PackageTree } from '../../services/Clients/PackageTree';
import ClickLabel from '../Ui/ClickLabel';
import './S3DialogSelector.css';
import { useS3Selection } from '../../hooks/useS3Selection';
import { usePopupVisibility } from '../../hooks/usePopopVisibility';
import BranchOptions from './BranchOptions';
import PackageOptions from './PackageOptions';
import LoadingIndicator from '../Ui/LoadingIndicator';

interface IProps {
  readonly label: string;
  readonly packageTree: PackageTree;
  readonly currentBranch: string;
  readonly currentVersion: string;
  readonly onSelectionChange: (data: { branch: PackageDetails | null, version: PackageDetails | null }) => void;
}

const S3DialogSelector: React.FC<IProps> = ({ packageTree, currentVersion, currentBranch, onSelectionChange }) => {
  const {
    isLoaded,
    loadError,
    retry,
    selectedBranch,
    selectedPackage,
    selectBranch,
    selectPackage,
  } = useS3Selection(packageTree, currentBranch, currentVersion);

  const {
    branchesOpen,
    packagesOpen,
    toggleBranches,
    togglePackages,
    closeBranches,
    closePackages,
  } = usePopupVisibility();

  useEffect(() => {
    if (isLoaded) {
      onSelectionChange({ branch: selectedBranch, version: selectedPackage });
    }
  }, [selectedBranch, selectedPackage, onSelectionChange, isLoaded]);

  const handleBranchChange = useCallback((branchFolder: string) => {
    selectBranch(branchFolder);
    closeBranches();
  }, [selectBranch, closeBranches]);

  const handlePackageChange = useCallback((packageFolder: string) => {
    selectPackage(packageFolder);
    closePackages();
  }, [selectPackage, closePackages]);

  if (!isLoaded) {
    return (
      <div className="scolab-switcher-s3dialogselector scolab-switcher-s3dialogselector-loading">
        <LoadingIndicator label="Loading selections" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="scolab-switcher-s3dialogselector">
        <p><i>Unable to load selections.</i></p>
        <button type="button" className="scolab-switcher-button" onClick={retry}>Retry</button>
      </div>
    );
  }

  return (
    <div className="scolab-switcher-s3dialogselector">
      {branchesOpen && (<BranchOptions branches={packageTree.getBranches()} selected={selectedBranch} onChange={handleBranchChange} />)}
      {packagesOpen && selectedBranch && (<PackageOptions packages={packageTree.getVersionsByBranch(selectedBranch)} selected={selectedPackage} onChange={handlePackageChange} />)}
      <ClickLabel label="Branch"
        title={selectedBranch!.label}
        value={selectedBranch!.label}
        onClick={toggleBranches} />
      <ClickLabel label="Version"
        title={`${selectedPackage?.label}`}
        value={selectedPackage?.label}
        onClick={togglePackages} />
    </div>
  );
};

export default S3DialogSelector;